import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2, ShoppingBag, ExternalLink, Sparkles, Lock, Search, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { dealerListings, buildImageUrl } from '../../services/dealerApi';
import { toast } from '../../hooks/use-toast';
import CreateReelModal from '../../components/dealers/CreateReelModal';
import UpgradeRequiredModal from '../../components/dealers/UpgradeRequiredModal';
import { usePlan } from '../../hooks/usePlan';

const STATUS_TABS = [
  { id: 'all', label: 'All' },
  { id: 'published', label: 'Live' },
  { id: 'pending_approval', label: 'Pending' },
  { id: 'draft', label: 'Drafts' },
  { id: 'sold', label: 'Sold' },
];

const Inventory = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [soldDialog, setSoldDialog] = useState(null);
  const [soldForm, setSoldForm] = useState({ name: '', mobile: '' });
  const [reelListing, setReelListing] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [query, setQuery] = useState('');
  const { hasFeature } = usePlan();

  const handleReelClick = (listing) => {
    if (hasFeature('reels_enabled')) {
      setReelListing(listing);
    } else {
      setUpgradeOpen(true);
    }
  };

  useEffect(() => { load(); }, [tab]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await dealerListings.list(tab === 'all' ? undefined : tab);
      setListings(res.listings || []);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this listing?')) return;
    await dealerListings.remove(id);
    toast({ title: 'Listing deleted' });
    load();
  };

  const handleMarkSold = async () => {
    if (!soldDialog || !soldForm.mobile) {
      toast({ title: 'Mobile required', variant: 'destructive' });
      return;
    }
    try {
      await dealerListings.markSold(soldDialog.id, { name: soldForm.name || null, mobile: soldForm.mobile, listing_id: soldDialog.id });
      toast({ title: 'Marked as sold' });
      setSoldDialog(null);
      setSoldForm({ name: '', mobile: '' });
      load();
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const statusBadge = (s) => {
    const map = {
      published: 'bg-green-500/20 text-green-300 border-green-500/30',
      pending_approval: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
      draft: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
      ai_processing: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
      sold: 'bg-blue-500/20 text-blue-300 border-blue-500/30',    };
    return map[s] || 'bg-slate-500/20 text-slate-300 border-slate-500/30';
  };

  // Client-side text search over make / model / year / fuel / transmission /
  // body / description. Tokens are ANDed, case-insensitive.
  const filteredListings = useMemo(() => {
    const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!tokens.length) return listings;
    return listings.filter((l) => {
      const hay = [
        l.ai_details?.make, l.ai_details?.model, l.ai_details?.variant,
        l.ai_details?.body_type, l.ai_details?.transmission,
        l.fuel_type, l.year, l.description,
      ].filter(Boolean).join(' ').toLowerCase();
      return tokens.every((t) => hay.includes(t));
    });
  }, [listings, query]);

  return (
    <div data-testid="inventory-page" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <span className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-medium">Inventory</span>
          <h1 className="mt-1 text-3xl md:text-4xl font-extrabold text-white tracking-tight">Your Listings</h1>
        </div>
        <Link to="/dealers/add-car">
          <Button data-testid="inventory-add-btn" className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-xl">
            <Plus className="w-4 h-4 mr-2" /> Add Car
          </Button>
        </Link>
      </div>

      {/* Search + Tabs */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search make, model, year…"
            data-testid="inventory-search"
            className="w-full pl-10 pr-9 py-2 rounded-xl bg-slate-900/60 border border-slate-700 text-white text-sm placeholder-slate-500 focus:border-cyan-500 outline-none"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Clear search"
              data-testid="inventory-search-clear"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {STATUS_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`inventory-tab-${t.id}`}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                tab === t.id
                  ? 'bg-cyan-500 text-slate-950'
                  : 'bg-slate-900/60 border border-slate-700 text-slate-300 hover:border-cyan-500/40'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Listings */}
      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading...</div>
      ) : listings.length === 0 ? (
        <div className="bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-12 text-center">
          <p className="text-slate-400">No listings yet.</p>
          <Link to="/dealers/add-car" className="mt-4 inline-block">
            <Button className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-xl">Add your first car</Button>
          </Link>
        </div>
      ) : filteredListings.length === 0 ? (
        <div className="bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-12 text-center" data-testid="inventory-empty-search">
          <p className="text-slate-400">No listings match &ldquo;<span className="text-white font-semibold">{query}</span>&rdquo; in this tab.</p>
          <button onClick={() => setQuery('')} className="mt-3 text-cyan-400 hover:text-cyan-300 text-sm font-semibold">Clear search</button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredListings.map((l) => {
            const photo = l.photos?.find(p => p.enhanced_url)?.enhanced_url || l.photos?.[0]?.url;
            return (
              <div key={l.id} data-testid={`listing-card-${l.id}`} className="bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl overflow-hidden hover:border-cyan-500/40 transition-colors">
                <div className="aspect-video bg-slate-800 relative">
                  {photo && <img src={buildImageUrl(photo)} alt="" className="w-full h-full object-cover" />}
                  <span className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${statusBadge(l.status)}`}>
                    {l.status.replace(/_/g, ' ')}
                  </span>
                </div>
                <div className="p-4 space-y-2">
                  <div>
                    <div className="font-bold text-white truncate">{l.ai_details?.make || 'Unknown'} {l.ai_details?.model || ''}</div>
                    <div className="text-xs text-slate-400">{l.year} · {l.fuel_type}{l.km_driven ? ` · ${l.km_driven.toLocaleString('en-IN')} km` : ''}</div>
                  </div>
                  <div className="text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400 tracking-tight">
                    ₹{(l.price || 0).toLocaleString('en-IN')}
                  </div>
                  <div className="flex gap-2 text-xs text-slate-500">
                    <span>{l.views || 0} views</span>
                    <span>·</span>
                    <span>{l.whatsapp_clicks || 0} WA</span>
                    <span>·</span>
                    <span>{l.call_clicks || 0} calls</span>
                  </div>
                  <div className="flex gap-2 pt-2 flex-wrap">
                    {(l.status === 'pending_approval' || l.status === 'draft') ? (
                      <Link to={`/dealers/inventory/${l.id}/approve`} className="flex-1">
                        <Button size="sm" className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold">
                          <Edit className="w-3 h-3 mr-1" /> Review
                        </Button>
                      </Link>
                    ) : l.status === 'published' ? (
                      <Button size="sm" onClick={() => setSoldDialog(l)} className="flex-1 bg-green-500 hover:bg-green-400 text-slate-950 font-semibold">
                        <ShoppingBag className="w-3 h-3 mr-1" /> Mark Sold
                      </Button>
                    ) : null}
                    {(l.photos?.length || 0) > 0 && (
                      <Button
                        size="sm"
                        onClick={() => handleReelClick(l)}
                        data-testid={`inventory-reel-btn-${l.id}`}
                        className={
                          hasFeature('reels_enabled')
                            ? 'bg-gradient-to-r from-fuchsia-500 to-pink-500 hover:from-fuchsia-400 hover:to-pink-400 text-white font-semibold'
                            : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700 font-semibold border border-slate-600'
                        }
                        title={hasFeature('reels_enabled') ? 'Generate Instagram reel' : 'Upgrade to Grow Plan to unlock'}
                      >
                        {hasFeature('reels_enabled') ? (
                          <><Sparkles className="w-3 h-3 mr-1" /> Reel</>
                        ) : (
                          <><Lock className="w-3 h-3 mr-1" /> Reel</>
                        )}
                      </Button>
                    )}
                    <Link to={`/dealers/inventory/${l.id}/approve`}>
                      <Button size="sm" variant="outline" className="border-slate-700 bg-slate-900/40 text-slate-200">
                        <ExternalLink className="w-3 h-3" />
                      </Button>
                    </Link>
                    <Button size="sm" variant="outline" onClick={() => handleDelete(l.id)} className="border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Mark Sold Dialog */}
      <Dialog open={!!soldDialog} onOpenChange={() => setSoldDialog(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Mark as Sold</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-400 mb-1">Customer Name (optional)</label>
              <Input value={soldForm.name} onChange={(e) => setSoldForm({ ...soldForm, name: e.target.value })} className="bg-slate-950/60 border-slate-700" data-testid="sold-name-input" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-400 mb-1">Customer Mobile *</label>
              <Input value={soldForm.mobile} onChange={(e) => setSoldForm({ ...soldForm, mobile: e.target.value })} maxLength={15} className="bg-slate-950/60 border-slate-700" data-testid="sold-mobile-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSoldDialog(null)} className="border-slate-700 bg-slate-900/40 text-slate-200">Cancel</Button>
            <Button onClick={handleMarkSold} className="bg-green-500 hover:bg-green-400 text-slate-950 font-semibold" data-testid="sold-confirm-btn">Confirm Sold</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Reel generation modal */}
      <CreateReelModal
        open={!!reelListing}
        onOpenChange={(v) => !v && setReelListing(null)}
        listing={reelListing}
        onCreated={() => setReelListing(null)}
      />

      <UpgradeRequiredModal
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        feature="reels_enabled"
      />
    </div>
  );
};

export default Inventory;
